import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from '../shared/layout/AppLayout.tsx';
import { AiPanelProvider } from '../features/ai/AiPanelContext.tsx';
import { HermesSessionsProvider } from '../features/hermes/HermesSessionsContext.tsx';
import { JobsProvider } from '../features/jobs/JobsContext.tsx';
import { NotificationsProvider } from '../features/notifications/NotificationsContext.tsx';
import { LoginPage } from '../features/auth/LoginPage.tsx';
import { TelegramLinkPage } from '../features/telegram/TelegramLinkPage.tsx';
import { ProtectedRoute } from '../features/auth/ProtectedRoute.tsx';
import { SpacesHomePage } from '../features/spaces/SpacesHomePage.tsx';
import { SpaceSettingsPage } from '../features/spaces/SpaceSettingsPage.tsx';
import { SpacePage } from '../features/spaces/SpacePage.tsx';
import { DictionariesPage } from '../features/dictionaries/DictionariesPage.tsx';
import { MyPage } from '../features/my/MyPage.tsx';
import { DictionaryDetailPage } from '../features/dictionaries/DictionaryDetailPage.tsx';
import { ProfilePage } from '../features/profile/ProfilePage.tsx';
import { ReleasesPage } from '../features/releases/ReleasesPage.tsx';
import { ItemRedirectPage } from '../features/items/ItemRedirectPage.tsx';
import { AdminSettingsPage } from '../features/admin/AdminSettingsPage.tsx';
import { AdminUsersPage } from '../features/admin/AdminUsersPage.tsx';
import { ActivityLogPage } from '../features/admin/ActivityLogPage.tsx';
import { NotFoundPage } from '../shared/components/NotFoundPage.tsx';

/**
 * Корневой компонент приложения. `/login` рендерится без лэйаута
 * и без ProtectedRoute. Всё остальное заворачивается в
 * <ProtectedRoute><AppLayout>…</AppLayout></ProtectedRoute>.
 *
 * M2: добавлены роуты пространств.
 *   /              — список пространств (SpacesHomePage)
 *   /spaces/:id/settings — настройки пространства (SpaceSettingsPage)
 *
 * M3: добавлены роуты справочников.
 *   /dictionaries          — список справочников (DictionariesPage)
 *   /dictionaries/:id      — детали справочника (DictionaryDetailPage)
 *
 * M4/M5: добавлены роуты записей.
 *   /spaces/:id      — главная страница пространства с таблицей записей (SpacePage)
 */
export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Страница привязки Telegram открывается по кнопке из чата, до входа:
          собственный гвард внутри вернёт на /login и приведёт обратно
          вместе с подписанными параметрами. */}
      <Route path="/telegram/link" element={<TelegramLinkPage />} />
      <Route path="/telegram/link/:code" element={<TelegramLinkPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AiPanelProvider>
            <HermesSessionsProvider>
            <JobsProvider>
            <NotificationsProvider>
            <AppLayout>
              <Routes>
                <Route path="/" element={<SpacesHomePage />} />
                <Route path="/my" element={<MyPage />} />
                <Route path="/c/:spaceSlug/:itemKey" element={<ItemRedirectPage />} />
                {/* Короткие человекочитаемые ссылки на пространства: `/s/:slug`.
                    Резолв slug→uuid внутри SpacePage/SpaceSettingsPage. Legacy
                    `/spaces/:id` сохранены и авто-апгрейдятся до slug-URL. */}
                <Route path="/s/:spaceSlug" element={<SpacePage />} />
                <Route path="/s/:spaceSlug/views/:viewId" element={<SpacePage />} />
                <Route path="/s/:spaceSlug/settings" element={<SpaceSettingsPage />} />
                <Route path="/spaces/:id" element={<SpacePage />} />
                <Route path="/spaces/:id/views/:viewId" element={<SpacePage />} />
                <Route path="/spaces/:id/settings" element={<SpaceSettingsPage />} />
                <Route path="/dictionaries" element={<DictionariesPage />} />
                <Route path="/dictionaries/:id" element={<DictionaryDetailPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/releases" element={<ReleasesPage />} />
                {/* Инструкция переехала в профиль, рядом с токенами. */}
                <Route path="/mcp-guide" element={<Navigate to="/profile#mcp" replace />} />
                <Route path="/admin/users" element={<AdminUsersPage />} />
                <Route path="/admin/activity" element={<ActivityLogPage />} />
                <Route path="/admin/settings" element={<AdminSettingsPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AppLayout>
            </NotificationsProvider>
            </JobsProvider>
            </HermesSessionsProvider>
            </AiPanelProvider>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
